import moment from "moment";
import { useAppSelector } from "../../effects/useAppSelector";
import { SubscriptionFeature } from "../../types/billing/SubscriptionFeature";
import { dateTimeUtils, isRequesting, isRequestSuccess } from "../../utils";
import { EvalActionBlocker } from "../access/EvalActionBlocker";
import { OnHoverTooltip } from "../common/OnHoverTooltip";
import { EvalFilter, EvalOrderByColumn } from "../../types/state/entities/EvalState";
import { useEvalSubscription } from "./hooks/userEvalSubscription";
import { convertToApiCriteria } from "../../utils/filtering/filter.utils";
import ExportButton from "../common/ExportButton";
import { useAppDispatch } from "../../effects/useAppDispatch";
import { evalActions } from "../../actions/entities/eval.actions";
import { DateTimeStamp } from "../../types/primitives/DateTimeStamp";


interface Props {
    holidays: DateTimeStamp[];
    sortingField: EvalOrderByColumn;
    sortDirection: string;
    disabled: boolean;
}

export function EvalExportButton({ holidays, sortingField, sortDirection, disabled }: Props) {
    const dispatch = useAppDispatch();

    const requestState = useAppSelector(s => s.entities.eval.requestState);
    const exportRequestState = useAppSelector(s => s.entities.eval.exportRequestState);
    const evalDataLength = useAppSelector(s => s.entities.eval.items.length);
    const lastAppliedFilter = useAppSelector(s => s.filters.eval.lastAppliedFilter as unknown as EvalFilter);

    const { isTrial, isTrialEnded } = useEvalSubscription();

    const getFilterLimitationTooltip = () => {
        const { dateFrom, dateTo } = lastAppliedFilter?.date.filter.selectedOption
            ? convertToApiCriteria(lastAppliedFilter.date.filter.selectedOption, lastAppliedFilter.date.filter.options ?? [])
            : { dateFrom: undefined, dateTo: undefined };

        const isSingleDayRange = dateFrom != null && dateTo != null && moment(dateFrom).isSame(dateTo, 'day');

        if (isTrial && !isTrialEnded && (!isSingleDayRange || !moment(dateFrom).isSame(dateTimeUtils.getLastBusinessDayOfPreviousMonth(holidays), 'day'))) {
            return "Select a prior month-end date in the calendar to enable export";
        } else if (!isTrial && !isSingleDayRange) {
            return "Select a single date in the filter calendar to enable export";
        }

        return null;
    }

    const filterLimitationTooltip = getFilterLimitationTooltip();

    return (
        <EvalActionBlocker feature={SubscriptionFeature.CanExportEvalData}>
            {blocked =>
                <OnHoverTooltip disabled={blocked || !evalDataLength} overlay={filterLimitationTooltip}>
                    <ExportButton
                        isExportLoad={isRequesting(exportRequestState)}
                        onExport={() => dispatch(evalActions.exportData(sortingField, sortDirection))}
                        disabled={
                            disabled ||
                            !isRequestSuccess(requestState) ||
                            lastAppliedFilter == null ||
                            !evalDataLength ||
                            !!filterLimitationTooltip
                        }
                    />
                </OnHoverTooltip>}
        </EvalActionBlocker>
    );
}
